import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./Project.module.css";
import {
	ProjectReqReview,
	ProjectReqIssues,
	ProjectDocsInfo,
	ProjectTeamInfo,
	ProjectSummary,
	ProjectSetup,
} from "../../components";
import { DashboardService } from "../../../../api";
import { checkValidity, exportView } from "../../../../navigation";
import { FavoriteCategoriesDialog, withWebsocket } from "../../../../common/components";
import {
	Flags,
	hasCompanyPermission,
	hasPermission,
	isSegFeatureEnabled,
	Permissions,
	SegFlags,
	translate,
} from "../../../../common/providers";
import { useApi, useTrackEvent } from "../../../../common/hooks";
import { INFORMATION_BOX_KEYS } from "../../constants/constants";
import { convertAnyDateToDisplayDate } from "../../../../common/utils";

function Project({ subscribeWS }) {
	const { call: getProjectPartners } = useApi(DashboardService.getProjectPartners);
	const { call: getProjectDocuments } = useApi(DashboardService.getProjectDocuments);
	const { call: getProjectRequirements } = useApi(DashboardService.getProjectRequirements);
	const { call: getProjectRequirementsValidation } = useApi(DashboardService.getProjectRequirementsValidation);
	const { call: getProjectSummary } = useApi(DashboardService.getProjectSummary);
	const { call: generateProjectSummary } = useApi(DashboardService.generateProjectSummary);
	const { call: updateProjectSummary } = useApi(DashboardService.updateProjectSummary);
	const [stateProject, setStateProject] = useState(null);
	const [selectedProjectName, setSelectedProjectName] = useState("");
	const [openProjectId, setOpenProjectId] = useState(0);
	const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1200);
	const [summary, setSummary] = useState(null);
	const [isGenerating, setIsGenerating] = useState(false);
	const navigate = useNavigate();
	const trackEvent = useTrackEvent();
	const projectId = useSelector(({ context }) => context.project?.id);
	const projectName = useSelector(({ context }) => context.project?.name);
	const companyId = useSelector(({ context }) => context.company?.id);
	const columns = useMemo(() => {
		if (isSmallScreen) {
			return [
				[INFORMATION_BOX_KEYS.INITIALIZATION, INFORMATION_BOX_KEYS.TEAM],
				[
					INFORMATION_BOX_KEYS.SUMMARY,
					INFORMATION_BOX_KEYS.REQ_REVIEW,
					INFORMATION_BOX_KEYS.REQ_VALIDATION,
					INFORMATION_BOX_KEYS.DOCUMENTS,
				],
			];
		}
		return [
			[INFORMATION_BOX_KEYS.INITIALIZATION],
			[INFORMATION_BOX_KEYS.SUMMARY, INFORMATION_BOX_KEYS.DOCUMENTS],
			[INFORMATION_BOX_KEYS.REQ_REVIEW, INFORMATION_BOX_KEYS.REQ_VALIDATION, INFORMATION_BOX_KEYS.TEAM],
		];
	}, [isSmallScreen]);

	const getProjectInformation = useCallback(() => {
		if (!projectId) {
			return;
		}
		const variables = { projectId, companyId };
		const requests = [
			getProjectDocuments(variables),
			getProjectRequirements(variables),
			getProjectRequirementsValidation(variables),
			getProjectPartners(variables),
		];

		const includeSummary = isSegFeatureEnabled(SegFlags.AI_PROJECT_SUMMARY);
		if (includeSummary) {
			requests.push(getProjectSummary({ projectId }));
		}
		Promise.allSettled(requests)
			.then((results) => {
				const [docsRes, reqRes, validRes, partnersRes, summaryRes] = results;
				setStateProject((prev) => ({
					...prev,
					documents: docsRes.status === "fulfilled" ? docsRes.value : null,
					projectReq: reqRes.status === "fulfilled" ? reqRes.value : null,
					projectReqValid: validRes.status === "fulfilled" ? validRes.value : null,
					partners: partnersRes.status === "fulfilled" ? partnersRes.value : null,
				}));
				if (includeSummary && summaryRes?.status === "fulfilled") {
					setSummary(summaryRes.value);
				}
			})
			.catch((err) => {
				console.error("Unexpected error in getProjectInformation:", err);
				setStateProject(null);
			});
	}, [
		getProjectDocuments,
		getProjectRequirements,
		getProjectRequirementsValidation,
		getProjectPartners,
		getProjectSummary,
		projectId,
		companyId,
	]);
	const handleGenerate = useCallback(() => {
		setIsGenerating(true);
		generateProjectSummary({ projectId })
			.then(() => trackEvent("Project_Dashboard_Summary_AI_Execution"))
			.catch(console.error);
	}, [generateProjectSummary, projectId, trackEvent]);
	const handleUpdate = useCallback(
		(value) => {
			updateProjectSummary({ projectId }, { value })
				.then((data) => {
					setSummary({
						...data,
						modifiedElapsed: { unit: "SECONDS", value: 3 },
						isEdited: true,
						lastModified: convertAnyDateToDisplayDate(new Date()),
					});
					trackEvent("Project_Dashboard_Summary_Edit");
				})
				.catch(console.error);
		},
		[projectId, updateProjectSummary, trackEvent]
	);

	const informationBoxes = useMemo(
		() => [
			{
				key: INFORMATION_BOX_KEYS.INITIALIZATION,
				element: <ProjectSetup className={styles.infoBox} />,
				displayed:
					hasPermission(Permissions.PROJECT_LEADER) && hasCompanyPermission(Permissions.PROJECT_LEADER),
			},
			{
				key: INFORMATION_BOX_KEYS.SUMMARY,
				element: (
					<ProjectSummary
						className={styles.infoBox}
						isGenerating={isGenerating}
						summary={summary}
						onGenerate={handleGenerate}
						onUpdate={handleUpdate}
					/>
				),
				segFlag: [SegFlags.AI_PROJECT_SUMMARY],
				displayed: true,
			},
			{
				key: INFORMATION_BOX_KEYS.REQ_REVIEW,
				element: (
					<ProjectReqReview
						isInProject
						className={styles.infoBox}
						clientReq={stateProject?.projectReq?.clientReq}
						coverage={stateProject?.projectReq?.coverage}
						newReq={stateProject?.projectReq?.newReq}
						projectId={openProjectId}
						reviewed={stateProject?.projectReq?.reviewed}
						totalReq={stateProject?.projectReq?.totalReq}
						onChangeUrl={navigate}
					/>
				),
				flag: [Flags.REQ_REVIEW],
				segFlag: [SegFlags.SMART_REVIEW, SegFlags.PROJECT_REQUIREMENTS],
				displayed: !!stateProject?.projectReq,
			},
			{
				key: INFORMATION_BOX_KEYS.REQ_VALIDATION,
				element: (
					<ProjectReqIssues
						isInProject
						accepted={stateProject?.projectReqValid?.accepted}
						className={styles.infoBox}
						denied={stateProject?.projectReqValid?.denied}
						nature={selectedProjectName}
						notme={stateProject?.projectReqValid?.notme}
						pending={stateProject?.projectReqValid?.pending}
						projectId={openProjectId}
						unreviewed={stateProject?.projectReqValid?.unvalidated}
						unvalidated={stateProject?.projectReqValid?.unvalidated}
						onChangeUrl={navigate}
					/>
				),
				flag: [Flags.REQ_REVIEW],
				segFlag: [SegFlags.SMART_REVIEW, SegFlags.PROJECT_REQUIREMENTS],
				displayed: !!stateProject?.projectReqValid,
			},
			{
				key: INFORMATION_BOX_KEYS.DOCUMENTS,
				element: (
					<ProjectDocsInfo
						isInProject
						className={styles.infoBox}
						failed={stateProject?.documents?.failed}
						inUpload={stateProject?.documents?.inProgress}
						pending={stateProject?.documents?.pending}
						projectId={openProjectId}
						timeout={stateProject?.documents?.timeout}
						totaldocuments={stateProject?.documents?.totalDocuments}
						totalpages={stateProject?.documents?.totalPages}
						onChangeUrl={navigate}
					/>
				),
				displayed: !!stateProject?.documents,
			},
			{
				key: INFORMATION_BOX_KEYS.TEAM,
				element: (
					<ProjectTeamInfo
						isInProject
						className={styles.infoBox}
						partners={stateProject?.partners}
						projectId={openProjectId}
						onChangeUrl={navigate}
					/>
				),
				displayed: !!stateProject?.partners,

				segFlag: [SegFlags.TEAM],
				flag: [Flags.PARTNERS],
			},
		],
		[
			navigate,
			handleGenerate,
			handleUpdate,
			stateProject,
			isGenerating,
			summary,
			openProjectId,
			selectedProjectName,
		]
	);
	const handleAutomationMessage = useCallback(
		({ reason, featureName }) => {
			if (reason === "COMPLETED_SUCCESS" && featureName === "PROJECT_SUMMARY") {
				getProjectSummary({ projectId })
					.then((data) => {
						setSummary(data);
					})
					.catch(console.error)
					.finally(() => setIsGenerating(false));
			}
			if (reason === "COMPLETED_FAILURE" && featureName === "PROJECT_SUMMARY") {
				setIsGenerating(false);
			}
		},
		[getProjectSummary, projectId]
	);
	useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.innerWidth < 1200);
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);
	// Adding information boxes To the shortest column
	useEffect(() => {
		const documentTitle = translate("dashboard.project.document.title");
		document.title = documentTitle;
		trackEvent("Project_Dashboard_visit");
		// eslint-disable-next-line
	}, []);
	useEffect(() => {
		if (projectId && companyId) {
			setOpenProjectId(projectId);
			setSelectedProjectName(projectName);
			getProjectInformation();
		}
	}, [projectId, projectName, getProjectInformation, companyId]);
	useEffect(() => {
		subscribeWS(`/topic/ai-agent/projects/${projectId}/refresh`, handleAutomationMessage);
	}, [subscribeWS, projectId, handleAutomationMessage]);
	return (
		<>
			<div className={styles.dashboardContainer}>
				<div className={styles.dashboard}>
					{columns.map((column, index) => {
						const key = index;
						return (
							<div key={key} className={styles.dashboard__column}>
								{column.map((cell) => {
									const informationBox = informationBoxes.find((ib) => ib.key === cell);
									if (checkValidity(informationBox) && informationBox.displayed) {
										return informationBox.element;
									}
									return null;
								})}
							</div>
						);
					})}
				</div>
			</div>
			{isSegFeatureEnabled(SegFlags.FAVORITE_CATEGORIES) && <FavoriteCategoriesDialog />}
		</>
	);
}
export default exportView({
	path: "/projects/:projectId/dashboard/project",
	localesPath: "/dashboards/dashboard-project/locales",
	component: withWebsocket(Project),
	segFlag: SegFlags.PROJECT_SUMMARY_DASHBOARD,
});
